import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { selectAdminToken } from '../../../admin/sessionSlice';
import { setBackButtonUrl } from '../../uiSlice';
import { addReportToServer } from '../../../functions/sendReport';
import { isVotingOpen } from '../../../utils/yogaTheme';
import { themeButtonBackground, themeButtonTextColor } from '../../../utils/colorUtils';
import SocialFeed from '../shared/SocialFeed';
import LikePopup from '../shared/LikePopup';
import RulesModal from '../shared/RulesModal';
import CategorySlider from '../shared/CategorySlider';
import {
  ACTIVE_GAMES,
  ALL_FILTER,
  EMAIL_RE,
  MESSAGE_MAX_LEN,
  apiPost,
  buildFreedomCategories,
  formatGameSubtitle,
  PENDING_APPROVAL_MESSAGE,
} from '../../../utils/gameHelpers';
import './freedomGame.css';

export default function FreedomGame() {
  const dispatch = useDispatch();
  const { data: themeData } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const categories = useMemo(() => buildFreedomCategories(themeData), [themeData]);
  const rules = useMemo(
    () => (Array.isArray(themeData?.rules) ? themeData.rules : []),
    [themeData]
  );

  const textColor = themeData?.text_color || '#000000';
  const likeBoxText = themeData?.like_box_text_color || '#ffffff';
  const likeBoxBg = themeData?.like_box_background_color || '#e25569';
  const btnBg = themeButtonBackground(themeData) || '#088e01';
  const btnText = themeButtonTextColor(themeData) || '#ffffff';
  const votingOpen = isVotingOpen(themeData);
  const defaultMsg = String(themeData?.deafult_message_text ?? '');

  const labels = {
    name: themeData?.lable_name_text || 'Your Name',
    colleagueName: themeData?.lable_colleague_name_text || 'Colleague Name',
    colleagueEmail: themeData?.lable_colleague_email_text || 'Colleague Email',
    badge: themeData?.lable_badge_text || 'Select Freedom Badge',
  };

  const [name, setName] = useState('');
  const [colleagueName, setColleagueName] = useState('');
  const [colleagueEmail, setColleagueEmail] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [likingId, setLikingId] = useState(null);
  const [likePopupMode, setLikePopupMode] = useState(null);
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER);
  const [page, setPage] = useState(1);
  const [elapsed, setElapsed] = useState(0);
  const [rulesOpen, setRulesOpen] = useState(false);
  const reportIdRef = useRef(null);

  const selectedCategory = categories[selectedIndex] || categories[0];

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || '/login?&save=true'));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    if (user?.name) setName((prev) => prev || user.name);
  }, [user?.name]);

  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const filterOptions = useMemo(() => {
    const names = categories.map((c) => c.name);
    return [ALL_FILTER, ...names];
  }, [categories]);

  const filteredPosts = useMemo(
    () => (activeFilter === ALL_FILTER ? posts : posts.filter((p) => p.category === activeFilter)),
    [posts, activeFilter]
  );

  const loadPosts = useCallback(async () => {
    if (!adminToken) {
      setLoadingPosts(false);
      return;
    }
    setLoadingPosts(true);
    try {
      const { ok, payload } = await apiPost('/gameGallery', adminToken, {
        userId: user?.userId || '',
        activeGame: ACTIVE_GAMES.FREEDOM,
        limit: 100,
      });
      setPosts(ok && payload?.success && Array.isArray(payload.posts) ? payload.posts : []);
    } catch (e) {
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  }, [adminToken, user?.userId]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleSubmit = async () => {
    if (!user || !adminToken) return;
    if (!name.trim()) {
      Swal.fire('Required', `${labels.name} is required.`, 'warning');
      return;
    }
    if (!selectedCategory?.name) {
      Swal.fire('Required', `${labels.badge} is required.`, 'warning');
      return;
    }
    if (!message.trim()) {
      Swal.fire('Required', 'Please write your freedom story.', 'warning');
      return;
    }
    if (message.trim().length > MESSAGE_MAX_LEN) {
      Swal.fire('Too long', `Message must be at most ${MESSAGE_MAX_LEN} characters.`, 'warning');
      return;
    }
    if (!colleagueName.trim()) {
      Swal.fire('Required', `${labels.colleagueName} is required.`, 'warning');
      return;
    }
    if (!colleagueEmail.trim() || !EMAIL_RE.test(colleagueEmail.trim())) {
      Swal.fire('Invalid email', 'Please enter a valid colleague email.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const { ok, payload } = await apiPost('/gameSubmit', adminToken, {
        userId: user.userId,
        name: name.trim(),
        email: user.email || '',
        colleagueName: colleagueName.trim(),
        colleagueEmail: colleagueEmail.trim(),
        category: selectedCategory.name,
        ecardName: selectedCategory.name,
        message: message.trim().slice(0, MESSAGE_MAX_LEN),
        icon: selectedCategory.icon,
        timeTaken: formatTime(elapsed),
        activeGame: ACTIVE_GAMES.FREEDOM,
        gameId: user.gameId || '',
        role: user.role || user.source || '',
      });

      if (!ok || !payload?.success) {
        Swal.fire(
          payload?.limitReached ? 'Limit reached' : 'Error',
          payload?.error || 'Submit failed.',
          payload?.limitReached ? 'info' : 'error'
        );
        return;
      }

      addReportToServer(user, {
        points: 0,
        reportTime: formatTime(elapsed),
        answers: [selectedCategory.name, 'freedom_story'],
        reportIdRef,
      });

      setColleagueName('');
      setColleagueEmail('');
      setMessage('');
      await loadPosts();
      if (payload.approval === 'pending') {
        Swal.fire('Submitted', PENDING_APPROVAL_MESSAGE, 'info');
      } else {
        Swal.fire('Success!', 'Your freedom story has been shared.', 'success');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (post) => {
    if (!votingOpen || !user || likingId) return;
    if (post.likedByMe) {
      setLikePopupMode('already');
      return;
    }
    setLikingId(post.id);
    try {
      const { ok, payload } = await apiPost('/yogaLike', adminToken, {
        userId: user.userId,
        gameId: post.id,
      });
      if (ok && payload?.success) {
        if (payload.liked) {
          setPosts((prev) =>
            prev.map((p) =>
              p.id === post.id
                ? { ...p, likedByMe: true, likeCount: payload.likeCount ?? (p.likeCount || 0) + 1 }
                : p
            )
          );
          setLikePopupMode('thanks');
        } else {
          setPosts((prev) =>
            prev.map((p) => (p.id === post.id ? { ...p, likedByMe: true } : p))
          );
          setLikePopupMode('already');
        }
      }
    } finally {
      setLikingId(null);
    }
  };

  const handleDelete = async (postId) => {
    if (!user?.userId || !adminToken) {
      Swal.fire('Error', 'You must be logged in to delete a post.', 'error');
      return;
    }
    const confirm = await Swal.fire({
      title: 'Delete this card?',
      text: 'Only you can delete your own posts.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      confirmButtonColor: '#e23b3b',
    });
    if (!confirm.isConfirmed) return;
    const { ok, payload } = await apiPost('/gameDelete', adminToken, {
      userId: user.userId,
      gameId: postId,
    });
    if (ok && payload?.success) {
      await loadPosts();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1400, showConfirmButton: false });
      return;
    }
    Swal.fire('Error', payload?.error || 'Could not delete this post.', 'error');
  };

  return (
    <div className="freedom-game cg-page" style={{ color: textColor, position: 'relative' }}>
      <button
        type="button"
        className="game-rules-top-btn"
        style={{ color: textColor, borderColor: textColor }}
        onClick={() => setRulesOpen(true)}
      >
        Rules
      </button>

      <h1 className="freedom-title" style={{ color: textColor }}>
        {themeData?.game_page_title || 'My Freedom'}
      </h1>
      <p className="freedom-subtitle" style={{ color: textColor }}>
        {formatGameSubtitle(themeData?.game_page_sub_title)}
      </p>

      {categories.length > 0 && (
        <CategorySlider
          items={categories}
          selectable
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
          showLabels={false}
          ariaLabel={labels.badge}
        />
      )}

      <div className="freedom-form" style={{ borderColor: textColor }}>
        <div className="freedom-field">
          <label style={{ color: textColor }}>{labels.name} *</label>
          <input type="text" value={name} readOnly />
        </div>
        <div className="freedom-field">
          <label style={{ color: textColor }}>{labels.badge} *</label>
          <select value={selectedIndex} onChange={(e) => setSelectedIndex(parseInt(e.target.value, 10))}>
            {categories.map((c, i) => (
              <option key={c.key} value={i}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="freedom-field">
          <label style={{ color: textColor }}>{labels.colleagueName} *</label>
          <input type="text" value={colleagueName} onChange={(e) => setColleagueName(e.target.value)} />
        </div>
        <div className="freedom-field">
          <label style={{ color: textColor }}>{labels.colleagueEmail} *</label>
          <input type="email" value={colleagueEmail} onChange={(e) => setColleagueEmail(e.target.value)} />
        </div>
        <div className="freedom-field freedom-field--message">
          <label style={{ color: textColor }}>Your message:- *</label>
          <div className="freedom-msg-wrap">
            <textarea
              value={message}
              maxLength={MESSAGE_MAX_LEN}
              onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX_LEN))}
              rows={4}
              placeholder={defaultMsg || 'Write your freedom story…'}
              required
            />
            <div className="freedom-charcount" style={{ color: textColor }}>
              {message.length}/{MESSAGE_MAX_LEN} Characters
            </div>
          </div>
        </div>
        <button
          type="button"
          className="freedom-submit"
          style={{ backgroundColor: btnBg, color: btnText, borderColor: btnBg }}
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Submitting…' : 'Share'}
        </button>
      </div>

      <SocialFeed
        posts={filteredPosts}
        loading={loadingPosts}
        page={page}
        onPageChange={setPage}
        activeFilter={activeFilter}
        onFilterChange={(f) => {
          setActiveFilter(f);
          setPage(1);
        }}
        filterOptions={filterOptions}
        votingOpen={votingOpen}
        likingId={likingId}
        onLike={handleLike}
        onDelete={handleDelete}
        likeBoxBg={likeBoxBg}
        likeBoxText={likeBoxText}
        textColor={textColor}
      />

      <LikePopup mode={likePopupMode} onClose={() => setLikePopupMode(null)} />

      <RulesModal
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        rules={rules}
        btnBg={btnBg}
        btnText={btnText}
      />
    </div>
  );
}
